import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { TooltipBox } from "./ChartTooltips";

// Hoist all static chart config objects out of the render path so React
// gets stable references between renders. Without this each render builds
// new `{}` props (margin, tick style, cursor, etc.) which forces every
// Recharts child to reconcile from scratch — measurable jank on long
// per-day series.
const CHART_MARGIN = { top: 8, right: 16, left: 0, bottom: 8 };
const TICK_STYLE = { fill: "#5C746A", fontSize: 12, fontFamily: "JetBrains Mono" };
const AXIS_LINE_STYLE = { stroke: "#E6EBE8" };
const TOOLTIP_CURSOR = { fill: "#F0F4F2" };
const BAR_RADIUS = [6, 6, 0, 0];
const TOOLTIP_CONTENT = <TooltipBox />;

export function PatientVolumeChartCard({
  title,
  subtitle,
  data,
  avg,
  numDays,
  testid,
  wrapClass = "",
  showAvg = true,
  totalPatients,
  totalLabel = "Total patients",
}) {
  return (
    <section
      data-testid={testid}
      className={`bg-white border border-[#E6EBE8] rounded-xl p-6 ${wrapClass}`}
    >
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
            {title}
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-[#1B3B2E] mt-1">
            {subtitle}
          </h2>
        </div>
        <div className="flex flex-col items-start sm:items-end rounded-lg border border-[#E6EBE8] bg-[#F7F9F8] px-4 py-2.5">
          {showAvg ? (
            <>
              <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                Avg patients / day
              </div>
              <div className="font-mono-data text-2xl font-light tracking-tighter text-[#1B3B2E] leading-none mt-1">
                {avg.toFixed(1)}
              </div>
              <div className="text-[11px] text-[#8A9C93] mt-0.5">
                across {numDays} day{numDays === 1 ? "" : "s"}
              </div>
            </>
          ) : (
            <>
              <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.15em] text-[#5C746A]">
                {totalLabel}
              </div>
              <div className="font-mono-data text-2xl font-light tracking-tighter text-[#1B3B2E] leading-none mt-1">
                {totalPatients ?? 0}
              </div>
              <div className="text-[11px] text-[#8A9C93] mt-0.5">
                across {numDays} day{numDays === 1 ? "" : "s"}
              </div>
            </>
          )}
        </div>
      </div>
      {data.length > 8 ? (
        <div className="flex items-center justify-end gap-1.5 text-[10px] uppercase tracking-[0.15em] text-[#8A9C93] mb-1 sm:hidden">
          <span>Swipe</span>
          <span aria-hidden>→</span>
        </div>
      ) : null}
      {/* Chart — horizontally scrollable; each bar gets ~52px so labels never collide and are easy to tap */}
      <div className="-ml-2 overflow-x-auto overflow-y-hidden -mr-2 sm:mr-0">
        <div
          className="h-72"
          style={{
            minWidth: `${Math.max(320, data.length * 52 + 48)}px`,
            width: `${Math.max(320, data.length * 52 + 48)}px`,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={CHART_MARGIN}>
              <CartesianGrid vertical={false} stroke="#E6EBE8" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={TICK_STYLE}
                axisLine={AXIS_LINE_STYLE}
                tickLine={false}
                interval={0}
                height={28}
              />
              <YAxis
                tick={TICK_STYLE}
                axisLine={false}
                tickLine={false}
                width={36}
              />
              <Tooltip cursor={TOOLTIP_CURSOR} content={TOOLTIP_CONTENT} />
              {showAvg ? (
                <ReferenceLine
                  y={avg}
                  stroke="#D36C56"
                  strokeWidth={1.5}
                  strokeDasharray="5 4"
                  ifOverflow="extendDomain"
                  label={{
                    value: `Avg ${avg.toFixed(1)}`,
                    position: "right",
                    fill: "#D36C56",
                    fontSize: 11,
                    fontFamily: "JetBrains Mono",
                    offset: 8,
                  }}
                />
              ) : null}
              <Bar dataKey="Patients" fill="#2B5E4A" radius={BAR_RADIUS} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}
